import { useState, useEffect, useCallback, useRef } from 'react'

/**
 * Hook pour debouncer une valeur
 * Utile pour les champs de recherche qui déclenchent des appels API
 * 
 * @param value - Valeur à debouncer
 * @param delay - Délai en ms (défaut: 300ms)
 * @returns Valeur debouncée
 * 
 * @example
 * const [search, setSearch] = useState('')
 * const debouncedSearch = useDebounce(search, 400)
 * 
 * useQuery({
 *   queryKey: ['users', debouncedSearch],
 *   queryFn: () => usersApi.list({ search: debouncedSearch })
 * })
 */
export function useDebounce<T>(value: T, delay: number = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(timer)
    }
  }, [value, delay])

  return debouncedValue
}

/**
 * Hook pour créer une fonction debouncée
 * Utile quand on veut debouncer une callback
 * 
 * @param callback - Fonction à debouncer
 * @param delay - Délai en ms (défaut: 300ms)
 * @returns Fonction debouncée
 * 
 * @example
 * const debouncedSave = useDebouncedCallback((text) => {
 *   saveToServer(text)
 * }, 500)
 */
export function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number = 300
): (...args: Parameters<T>) => void {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const callbackRef = useRef(callback)

  // Update ref when callback changes
  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  const debouncedCallback = useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...args)
      }, delay)
    },
    [delay]
  )

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return debouncedCallback
}

export default useDebounce
